'use client';

import { useState, useEffect } from 'react';
import ShopHeader from './ShopHeader';
import ContactInfo from './ContactInfo';
import ServicesSection from './ServicesSection';
import QuoteRequestForm from './QuoteRequestForm';

interface MechanicData {
  shop: {
    name: string;
    logo: string;
    logoAlt: string;
    rating: number;
    reviewCount: number;
    verified: boolean;
    responseTime: string;
    yearsInBusiness: number;
  };
  contact: {
    phone: string;
    email: string;
    website: string;
    address: string;
    city: string;
    state: string;
    zip: string;
  };
  hours: {
    day: string;
    open: string;
    close: string;
    isOpen: boolean;
  }[];
  services: {
    name: string;
    description: string;
    priceRange: string;
    icon: string;
    specialization: boolean;
  }[];
  specializations: string[];
  reviews: {
    id: number;
    author: string;
    rating: number;
    date: string;
    highlight: string;
    serviceType: string;
  }[];
  images: {
    url: string;
    alt: string;
    caption: string;
  }[];
  certifications: {
    name: string;
    issuer: string;
    year: string;
    logo: string;
    logoAlt: string;
    description: string;
  }[];
  warranty: {
    parts: string;
    labor: string;
    details: string;
  };
}

const MechanicDetailInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [dbMechanic, setDbMechanic] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsHydrated(true);
    const id = new URLSearchParams(window.location.search).get('id');
    if (id) {
      fetch(`/api/mechanics/${id}`)
        .then(res => res.json())
        .then(data => {
          if (data?.data) {
            setDbMechanic(data.data);
          }
          setIsLoading(false);
        })
        .catch(err => {
          console.error("Failed to fetch mechanic", err);
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  const mechanicData: MechanicData = {
    shop: {
      name: dbMechanic?.business_name || "Bavarian Auto Specialists",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_115ec0dd0-1767475332869.png",
      logoAlt: "Bavarian Auto Specialists logo with blue and white BMW-inspired design",
      rating: dbMechanic?.rating ? Number(dbMechanic.rating) : 4.8,
      reviewCount: dbMechanic?.review_count ? Number(dbMechanic.review_count) : 247,
      verified: dbMechanic?.verified ?? true,
      responseTime: "2 hours",
      yearsInBusiness: 15
    },
    contact: {
      phone: dbMechanic?.phone || "(650) 555-0123",
      email: "service@" + (dbMechanic?.business_name?.toLowerCase().replace(/[^a-z0-9]/g, '') || "bavarianautosf") + ".com",
      website: "https://" + (dbMechanic?.business_name?.toLowerCase().replace(/[^a-z0-9]/g, '') || "bavarianautosf") + ".com",
      address: dbMechanic?.address || "1234 El Camino Real",
      city: dbMechanic?.city || "San Mateo",
      state: dbMechanic?.state || "CA",
      zip: dbMechanic?.zip_code || "94402"
    },
    hours: [
    { day: "Monday", open: "8:00 AM", close: "6:00 PM", isOpen: true },
    { day: "Tuesday", open: "8:00 AM", close: "6:00 PM", isOpen: true },
    { day: "Wednesday", open: "8:00 AM", close: "6:00 PM", isOpen: true },
    { day: "Thursday", open: "8:00 AM", close: "6:00 PM", isOpen: true },
    { day: "Friday", open: "8:00 AM", close: "6:00 PM", isOpen: true },
    { day: "Saturday", open: "9:00 AM", close: "4:00 PM", isOpen: false },
    { day: "Sunday", open: "Closed", close: "", isOpen: false }],

    services: [
    {
      name: "Oil Change & Filter",
      description: "Complete oil change service using premium synthetic oil recommended for BMW and Lexus vehicles. Includes multi-point inspection of fluid levels, tire pressure, and brake condition. We use OEM-quality filters and dispose of old oil responsibly.",
      priceRange: "$89 - $149",
      icon: "WrenchScrewdriverIcon",
      specialization: true
    },
    {
      name: "Brake Service",
      description: "Comprehensive brake system service including pad replacement, rotor resurfacing or replacement, brake fluid flush, and caliper inspection. We use premium brake components that meet or exceed OEM specifications for optimal stopping power.",
      priceRange: "$299 - $899",
      icon: "ShieldCheckIcon",
      specialization: true
    },
    {
      name: "Engine Diagnostics",
      description: "Advanced computer diagnostics using factory-level scan tools to identify check engine lights, performance issues, and electrical problems. Includes detailed diagnostic report with recommended repairs and cost estimates.",
      priceRange: "$129 - $199",
      icon: "CpuChipIcon",
      specialization: true
    },
    {
      name: "Transmission Service",
      description: "Complete transmission fluid exchange, filter replacement, and pan gasket service. We use manufacturer-specified transmission fluid and follow proper service procedures to extend transmission life and maintain smooth shifting.",
      priceRange: "$249 - $499",
      icon: "Cog6ToothIcon",
      specialization: false
    },
    {
      name: "Suspension Repair",
      description: "Strut and shock replacement, control arm bushings, sway bar links, and alignment services. We restore factory ride quality and handling characteristics using premium suspension components designed for European vehicles.",
      priceRange: "$399 - $1,499",
      icon: "ArrowsUpDownIcon",
      specialization: true
    },
    {
      name: "Electrical Systems",
      description: "Battery testing and replacement, alternator service, starter repair, and electrical troubleshooting. We diagnose complex electrical issues using advanced diagnostic equipment and repair wiring harness problems.",
      priceRange: "$149 - $899",
      icon: "BoltIcon",
      specialization: false
    },
    {
      name: "AC Repair",
      description: "Air conditioning system diagnosis, refrigerant recharge, compressor replacement, and evaporator service. We use proper AC service equipment and follow EPA guidelines for refrigerant handling.",
      priceRange: "$199 - $1,299",
      icon: "CloudIcon",
      specialization: false
    },
    {
      name: "Tire Service",
      description: "Tire mounting, balancing, rotation, and alignment services. We offer premium tire brands suitable for BMW and Lexus vehicles and provide tire pressure monitoring system service.",
      priceRange: "$79 - $299",
      icon: "CircleStackIcon",
      specialization: false
    }],

    specializations: dbMechanic?.specializations?.filter((s: string) => 
      !s.includes('Service') && 
      !s.includes('Repair') && 
      !s.includes('Change') && 
      !s.includes('Diagnostics') && 
      !s.includes('Work') && 
      !s.includes('Replacement')
    ) || ["BMW", "Lexus", "European Vehicles"],
    reviews: [
    {
      id: 1,
      author: "Michael Chen",
      rating: 5,
      date: "2 weeks ago",
      highlight: "Excellent service on my BMW 3 Series. They diagnosed a complex electrical issue that the dealership couldn't figure out. Fair pricing and honest recommendations. Will definitely return for future maintenance.",
      serviceType: "Engine Diagnostics"
    },
    {
      id: 2,
      author: "Sarah Martinez",
      rating: 5,
      date: "1 month ago",
      highlight: "Best independent shop for Lexus in your area. They replaced my brake pads and rotors for half the dealership price. Work quality is outstanding and they use OEM parts. Highly recommend!",
      serviceType: "Brake Service"
    },
    {
      id: 3,
      author: "David Thompson",
      rating: 4,
      date: "1 month ago",
      highlight: "Great experience with oil change and inspection. They found a small coolant leak and fixed it promptly. Appreciate their thorough approach and clear communication about what needs attention.",
      serviceType: "Oil Change"
    },
    {
      id: 4,
      author: "Jennifer Wu",
      rating: 5,
      date: "2 months ago",
      highlight: "Trustworthy shop that doesn't oversell services. They prioritized what my BMW actually needed and saved me money. The waiting area is comfortable and they finished ahead of schedule.",
      serviceType: "General Maintenance"
    },
    {
      id: 5,
      author: "Robert Johnson",
      rating: 5,
      date: "2 months ago",
      highlight: "Outstanding transmission service on my Lexus ES. They explained everything clearly and the car shifts smoother than ever. Pricing was transparent with no hidden fees. Very professional team.",
      serviceType: "Transmission Repair"
    }],

    images: [
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_1e60a329f-1766520712222.png",
      alt: "Modern automotive repair shop interior with BMW vehicles on lifts and professional diagnostic equipment",
      caption: "State-of-the-art repair facility"
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_1e960a229-1766476774892.png",
      alt: "Professional mechanic in blue uniform using diagnostic computer on luxury vehicle",
      caption: "Advanced diagnostic capabilities"
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_11767edee-1765186966105.png",
      alt: "Clean organized automotive workshop with specialized tools and equipment for European vehicles",
      caption: "Specialized European vehicle tools"
    },
    {
      url: "https://images.unsplash.com/photo-1594505987127-947082dd9a23",
      alt: "Comfortable customer waiting area with modern furniture and large windows",
      caption: "Comfortable waiting area"
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_1b8c0a236-1767475333780.png",
      alt: "Mechanic performing brake service on BMW showing attention to detail",
      caption: "Precision brake service"
    },
    {
      url: "https://images.unsplash.com/photo-1598223181630-8a5814045768",
      alt: "Team of certified mechanics standing in front of repair shop",
      caption: "Experienced certified team"
    }],

    certifications: [
    {
      name: "ASE Master",
      issuer: "ASE",
      year: "2010",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1f46f5653-1766519693414.png",
      logoAlt: "ASE Master Technician certification badge with blue and red colors",
      description: "Automotive Service Excellence Master Certification"
    },
    {
      name: "BMW Specialist",
      issuer: "BMW",
      year: "2012",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1333f9f5b-1767475332881.png",
      logoAlt: "BMW specialist certification logo with iconic blue and white roundel",
      description: "Factory-trained BMW service specialist"
    },
    {
      name: "Lexus Certified",
      issuer: "Lexus",
      year: "2014",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_135aca835-1767475330829.png",
      logoAlt: "Lexus certified technician badge with silver and black design",
      description: "Lexus certified independent service provider"
    },
    {
      name: "Bosch Certified",
      issuer: "Bosch",
      year: "2015",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1ff170fe9-1767475330821.png",
      logoAlt: "Bosch automotive service certification logo in red and white",
      description: "Bosch automotive service excellence"
    }],

    warranty: {
      parts: "24 months / 24,000 miles",
      labor: "12 months / 12,000 miles",
      details: "All repairs are backed by our comprehensive warranty covering parts and labor. We stand behind our work and use quality components that meet or exceed OEM specifications. Warranty is valid at any of our locations and is transferable if you sell your vehicle."
    }
  };

  if (!isHydrated || isLoading) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="h-8 bg-muted rounded w-48 mb-8" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <div className="h-64 bg-muted rounded-xl" />
              <div className="h-96 bg-muted rounded-xl" />
            </div>
            <div className="space-y-8">
              <div className="h-96 bg-muted rounded-xl" />
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-16">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
        <ShopHeader shop={mechanicData.shop} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
          <div className="lg:col-span-2 space-y-8">
            <ServicesSection
              services={[]}
              specializations={mechanicData.specializations} />
          </div>

          <div className="space-y-8">
            <div className="lg:sticky lg:top-24 space-y-8">
              <ContactInfo
                contact={mechanicData.contact}
                hours={[]}
                mechanicName={mechanicData.shop.name} />

              <QuoteRequestForm mechanicName={mechanicData.shop.name} />
            </div>
          </div>
        </div>
      </div>
    </div>);
};

export default MechanicDetailInteractive;